import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchContentAnalytics,
  fetchContentGrowth,
} from "@/api/dashboard";
import { fetchEngagementTrends } from "@/api/user-analytics";
import { fetchLanguages } from "@/api/languages";
import StatsCard from "@/components/dashboard/StatsCard";
import { TrendingBooks } from "@/components/books/TrendingBooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import BarChart from "@/components/dashboard/BarChart";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DateRangeFilter } from "@/components/analytics/DateRangeFilter";
import { DateRange } from "@/api/business-analytics";
import {
  BarChart as ReBars,
  Bar as ReBar,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer as ReResponsiveContainer,
  Legend as ReLegend,
} from "recharts";
import {
  BookOpen,
  Headphones,
  Globe,
  BookIcon,
  Tag,
  PieChart,
  UserPen,
  BarChart2,
  TrendingUp,
  Eye,
  BookMarked,
  LayoutPanelTop,
} from "lucide-react";

// Get language badge color (reusing the function from BooksList)
const getLanguageBadgeColor = (code: string): string => {
  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  const codeNum = code
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = codeNum % colorPairs.length;

  return colorPairs[index];
};

const ContentAnalyticsDashboard = () => {
  // Date range for content growth (default current year)
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);
    return { from: startOfYear, to: endOfYear };
  });
  // Separate date range for engagement
  const [engagementRange, setEngagementRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);
    return { from: startOfYear, to: endOfYear };
  });

  // Fetch content analytics data
  const { data: contentData, isLoading } = useQuery({
    queryKey: ["contentAnalytics"],
    queryFn: fetchContentAnalytics,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  // Fetch content growth series based on date range
  const { data: contentGrowthData } = useQuery({
    queryKey: ["contentGrowth", dateRange],
    queryFn: () => fetchContentGrowth(dateRange),
    refetchInterval: 5 * 60 * 1000,
  });

  // Fetch engagement trends
  const { data: engagementTrends } = useQuery({
    queryKey: ["engagementTrends", engagementRange],
    queryFn: () => fetchEngagementTrends(engagementRange),
    refetchInterval: 5 * 60 * 1000,
  });

  // Fetch languages for displaying language names
  const { data: languages } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  // Get language name function
  const getLanguageName = (code: string) => {
    const language = languages?.find((lang) => lang.code === code);
    return language ? language.name : code;
  };

  if (isLoading) {
    return (
      <AdminLayout title="Content Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!contentData) {
    return (
      <AdminLayout title="Content Analytics">
        <div className="p-8 text-center">
          <h2 className="text-xl font-medium mb-2">
            Unable to load content analytics
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page.
          </p>
        </div>
      </AdminLayout>
    );
  }

  // Calculate metrics
  const bookActivationRate =
    contentData.totalBooks > 0
      ? Math.round((contentData.activeBooks / contentData.totalBooks) * 100)
      : 0;

  const audioCoverage =
    contentData.totalBooks > 0
      ? Math.round((contentData.booksWithAudio / contentData.totalBooks) * 100)
      : 0;

  // Simple grouped multi-series bar chart
  const engagementSeries = engagementTrends?.series || [];
  const MultiBarChart = ({ title }: { title: string }) => (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ReResponsiveContainer width="100%" height="100%">
            <ReBars
              data={engagementSeries}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <ReXAxis dataKey="month" />
              <ReYAxis />
              <ReTooltip />
              <ReLegend />
              <ReBar
                dataKey="views"
                name="Views"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <ReBar
                dataKey="reads"
                name="Reads"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
              />
              <ReBar
                dataKey="plays"
                name="Plays"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
              />
            </ReBars>
          </ReResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout title="Content Analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Content Analytics</h1>
            <p className="text-muted-foreground">
              Detailed insights into your content library and performance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" />
            <span className="text-sm text-muted-foreground">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>

        {/* Content Overview Metrics - compact combined cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Total Books (combined) */}
          <StatsCard
            title="Total Books"
            value={contentData.totalBooks}
            icon={<BookOpen className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {contentData.activeBooks}
                </span>{" "}
                active ·
                <span className="ml-1 font-medium text-foreground">
                  {contentData.booksWithAudio}
                </span>{" "}
                with audio
                <span className="ml-1">({audioCoverage}% coverage)</span>
              </div>
            }
          />

          {/* Total Categories */}
          <StatsCard
            title="Total Categories"
            value={contentData.categoriesCount}
            icon={<BookIcon className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {contentData.activeCategoriesCount || 0}
                </span>{" "}
                active
              </div>
            }
          />

          {/* Total People */}
          <StatsCard
            title="Total People"
            value={contentData.peopleCount}
            icon={<UserPen className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                {(contentData.peopleRolesCounts || [])
                  .slice(0, 3)
                  .map(
                    (
                      r: { name: string; count: number },
                      idx: number,
                      arr: { name: string; count: number }[]
                    ) => (
                      <span key={r.name}>
                        {r.name}:{" "}
                        <span className="font-medium text-foreground">
                          {r.count}
                        </span>
                        {idx < arr.length - 1 ? " · " : ""}
                      </span>
                    )
                  )}
                {contentData.peopleRolesCounts &&
                contentData.peopleRolesCounts.length > 3
                  ? " · …"
                  : ""}
              </div>
            }
          />

          {/* Total Collections */}
          <StatsCard
            title="Total Collections"
            value={contentData.collectionsCount}
            icon={<BookMarked className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {contentData.activeCollectionsCount || 0}
                </span>{" "}
                active · ~
                <span className="font-medium text-foreground">
                  {contentData.avgBooksPerCollection || 0}
                </span>{" "}
                per collection, min{" "}
                <span className="font-medium text-foreground">
                  {contentData.minBooksPerCollection || 0}
                </span>
                , max{" "}
                <span className="font-medium text-foreground">
                  {contentData.maxBooksPerCollection || 0}
                </span>
              </div>
            }
          />

          {/* Total Sections */}
          <StatsCard
            title="Total Sections"
            value={contentData.sectionsCount}
            icon={<LayoutPanelTop className="h-4 w-4" />}
            footer={
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium text-foreground">
                  {contentData.activeSectionsCount || 0}
                </span>{" "}
                active · ~
                <span className="font-medium text-foreground">
                  {contentData.avgBooksPerSection || 0}
                </span>{" "}
                per section, min{" "}
                <span className="font-medium text-foreground">
                  {contentData.minBooksPerSection || 0}
                </span>
                , max{" "}
                <span className="font-medium text-foreground">
                  {contentData.maxBooksPerSection || 0}
                </span>
              </div>
            }
          />
        </div>

        {/* Cleaner layout with tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="engagement">Engagement</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recently Added */}
              <Card className="border border-border">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">
                        Recently Added
                      </h4>
                      <div className="text-2xl font-bold mt-1">
                        {contentData.recentAdditions}
                      </div>
                    </div>
                    <div className="h-9 w-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <span className="text-green-600 dark:text-green-400 text-xs font-bold">
                        30d
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-muted-foreground">
                      {Math.round(
                        (contentData.recentAdditions /
                          (contentData.totalBooks || 1)) *
                          100
                      )}
                      % of total books
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-medium">This Week:</span>
                        <Badge variant="outline" className="text-xs">
                          {contentData.thisWeekAdditions}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-medium">Last Week:</span>
                        <Badge variant="outline" className="text-xs">
                          {contentData.lastWeekAdditions}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {contentData.latestBooks &&
                    contentData.latestBooks.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {contentData.latestBooks.map(
                          (b: {
                            id: number;
                            title: string;
                            created_at: string;
                          }) => (
                            <div
                              key={b.id}
                              className="flex items-center justify-between text-sm"
                            >
                              <div className="truncate pr-3 max-w-[70%]">
                                {b.title}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {Intl.DateTimeFormat
                                  ? // fallback relative time using toLocaleString if needed
                                    new Date(b.created_at).toLocaleDateString()
                                  : new Date(b.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Content Structure */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">
                    Summary Content Structure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-card rounded-lg p-4 border border-border">
                      <div className="text-xs text-muted-foreground">
                        Languages
                      </div>
                      <div className="text-xl font-bold mt-1">
                        {contentData.languageStats.length}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {contentData.languageStats.length === 0 &&
                          "No languages"}
                        {contentData.languageStats.length === 1 &&
                          getLanguageName(contentData.languageStats[0].code)}
                        {contentData.languageStats.length > 1 && (
                          <>
                            {contentData.languageStats.length} languages ·{" "}
                            {contentData.languageStats
                              .slice(0, 3)
                              .map((l: { code: string }) =>
                                getLanguageName(l.code)
                              )
                              .join(", ")}
                            {contentData.languageStats.length > 3 ? ", …" : ""}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="bg-card rounded-lg p-4 border border-border">
                      <div className="text-xs text-muted-foreground">
                        Summary Contents
                      </div>
                      <div className="text-xl font-bold mt-1">
                        {contentData.totalSummaryContents}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {contentData.booksWithAudio} with audio ({audioCoverage}
                        % coverage)
                      </div>
                    </div>
                    <div className="bg-card rounded-lg p-4 border border-border">
                      <div className="text-xs text-muted-foreground">
                        Chapters
                      </div>
                      <div className="text-xl font-bold mt-1">
                        {contentData.totalChapters}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        ~{contentData.avgChaptersPerBook} per book
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Most Viewed */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Eye className="h-4 w-4 mr-2 text-primary" />
                    Most Viewed Books
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(contentData.popularBooks || []).map(
                      (
                        book: { id: number; title: string; viewCount: number },
                        index: number
                      ) => (
                        <div
                          key={book.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 truncate pr-2 max-w-[80%]">
                            <div className="text-xs font-medium">
                              {index + 1}.
                            </div>
                            <div className="text-sm truncate">{book.title}</div>
                          </div>
                          <div className="text-xs whitespace-nowrap">
                            <Badge
                              variant="secondary"
                              className="text-xs font-medium"
                            >
                              {book.viewCount.toLocaleString()} views
                            </Badge>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Most Read */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-primary" />
                    Most Read Books
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(contentData.mostReadBooks || []).map(
                      (
                        book: { id: number; title: string; readCount: number },
                        index: number
                      ) => (
                        <div
                          key={book.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 truncate pr-2 max-w-[80%]">
                            <div className="text-xs font-medium">
                              {index + 1}.
                            </div>
                            <div className="text-sm truncate">{book.title}</div>
                          </div>
                          <div className="text-xs whitespace-nowrap">
                            <Badge
                              variant="secondary"
                              className="text-xs font-medium"
                            >
                              {book.readCount.toLocaleString()} reads
                            </Badge>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Most Played */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Headphones className="h-4 w-4 mr-2 text-primary" />
                    Most Played Books
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(contentData.mostPlayedBooks || []).map(
                      (
                        book: { id: number; title: string; playCount: number },
                        index: number
                      ) => (
                        <div
                          key={book.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 truncate pr-2 max-w-[80%]">
                            <div className="text-xs font-medium">
                              {index + 1}.
                            </div>
                            <div className="text-sm truncate">{book.title}</div>
                          </div>
                          <div className="text-xs whitespace-nowrap">
                            <Badge
                              variant="secondary"
                              className="text-xs font-medium"
                            >
                              {book.playCount.toLocaleString()} plays
                            </Badge>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top Categories */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Tag className="h-4 w-4 mr-2 text-primary" />
                    Top Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(contentData.topCategories || []).map(
                      (
                        category: { id: number; name: string; count: number },
                        index: number
                      ) => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-medium">
                              {index + 1}.
                            </div>
                            <div className="text-sm">{category.name}</div>
                          </div>
                          <div className="text-xs">
                            <Badge
                              variant="secondary"
                              className="text-xs font-medium"
                            >
                              {category.count}
                            </Badge>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="content">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border">
                <DateRangeFilter
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              </div>
              <div className="grid grid-cols-1 gap-6">
                {/* Content Growth Trend */}
                <BarChart
                  title="Content Growth Trend (Books Created per Month)"
                  data={contentGrowthData?.series || contentData.contentGrowth}
                  color="hsl(160, 84%, 39%)"
                  compact={false}
                />

                {/* Chapters moved to Overview */}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="engagement">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border mb-4">
                  <DateRangeFilter
                    dateRange={engagementRange}
                    onDateRangeChange={setEngagementRange}
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <Eye className="h-4 w-4 text-muted-foreground" /> Views
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {contentData.totalViews.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <BookOpen className="h-4 w-4 text-muted-foreground" />{" "}
                      Reads
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {(
                        contentData.totalReadsAcrossBooks || 0
                      ).toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <Headphones className="h-4 w-4 text-muted-foreground" />{" "}
                      Plays
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {(
                        contentData.totalPlaysAcrossBooks || 0
                      ).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <MultiBarChart title="Views, Reads, Plays (Grouped by Period)" />
                </div>

                <div className="mt-6">
                  <TrendingBooks
                    limit={20}
                    period="7d"
                    showPeriodSelector={true}
                    className=""
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ContentAnalyticsDashboard;
